import { Component, OnDestroy, computed, inject, signal } from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { CommonModule } from '@angular/common';
import { MenuItem } from '../../models/menu-item.model';
import { DebugModalComponent } from '../debug-modal/debug-modal.component';
import { SidebarStateService } from '../../services/sidebar-state.service';
import { Router } from '@angular/router';
import { ConfirmationService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { AuthStateService } from '../../../../modules/auth/presentation/state/auth.state';
import { AuthPermissionService } from '../../../../modules/auth/application/services/auth-permission.service';
import { AppPermission } from '../../../../modules/auth/domain/enums/app-permission.enum';
import { environment } from '../../../../../environments/environment';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, RouterLink, RouterLinkActive, DebugModalComponent, ConfirmDialogModule],
  providers: [ConfirmationService],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss',
})
export class SidebarComponent implements OnDestroy {
  protected readonly showDebugModal = signal(false);
  protected readonly isHoverExpanded = signal(false);

  protected readonly isExpanded = computed(
    () => !this.sidebarState.isCollapsed() || this.isHoverExpanded(),
  );

  protected readonly isOverlay = computed(
    () => this.sidebarState.isCollapsed() && this.isHoverExpanded(),
  );

  private readonly allPrimaryMenuItems: MenuItem[] = [
    { icon: 'pi pi-th-large', label: 'Panel de control', route: '/dashboard', permission: [AppPermission.UiDashboardComplete] },
    { icon: 'pi pi-briefcase', label: 'Carteras', route: '/portfolios', permission: [AppPermission.UiPortfolios] },
    { icon: 'pi pi-file', label: 'Solicitudes', route: '/orders', permission: [AppPermission.UiOrders] },
    { icon: 'pi pi-file-check', label: 'Controles de calidad', route: '/quality-controls', permission: [AppPermission.UiQualityControl] },
    { icon: 'pi pi-link', label: 'Conexiones', route: '/connections', permission: [AppPermission.UiCommunications] },
  ];

  private readonly allSecondaryMenuItems: MenuItem[] = [
    { icon: 'pi pi-users', label: 'Usuarios', route: '/users', permission: [AppPermission.UiUsers] },
    { icon: 'pi pi-user', label: 'Clientes', route: '/clients', permission: [AppPermission.UiClients] },
  ];

  protected readonly primaryMenuItems = computed(() =>
    this.allPrimaryMenuItems.filter((item) =>
      !item.permission?.length || this.permissionService.hasAnyPermission(item.permission)
    )
  );

  protected readonly secondaryMenuItems = computed(() =>
    this.allSecondaryMenuItems.filter((item) =>
      !item.permission?.length || this.permissionService.hasAnyPermission(item.permission)
    )
  );

  protected readonly currentUserName = computed(() => {
    const user = this.authStateService.currentUser();
    if (!user) return '';
    return user.fullName ?? [user.firstName, user.lastName].filter(Boolean).join(' ');
  });

  protected readonly permissionService = inject(AuthPermissionService);
  protected readonly AppPermission = AppPermission;
  protected readonly isProduction = environment.production;

  constructor(
    protected readonly sidebarState: SidebarStateService,
    private readonly router: Router,
    private readonly authStateService: AuthStateService,
    private readonly confirmationService: ConfirmationService,
  ) { }

  toggleOverlay(): void {
    this.isHoverExpanded.set(!this.isHoverExpanded());
  }

  onToggle(): void {
    if (window.innerWidth <= 1024) {
      this.toggleOverlay();
    } else {
      this.isHoverExpanded.set(false);
      if (this.sidebarState.isCollapsed()) {
        this.sidebarState.expand();
      } else {
        this.sidebarState.collapse();
      }
    }
  }

  ngOnDestroy(): void { }

  onLogout(): void {
    this.confirmationService.confirm({
      message: '¿Estás seguro de que deseas cerrar sesión?',
      header: 'Cerrar sesión',
      acceptLabel: 'Sí, cerrar sesión',
      acceptButtonStyleClass: 'p-button-link p-button-danger',
      rejectLabel: 'Cancelar',
      rejectButtonProps: 'p-button-link',
      accept: () => {
        this.authStateService.logout().subscribe({
          next: () => this.router.navigate(['/login']),
          error: () => this.router.navigate(['/login']),
        });
      },
    });
  }
}
